import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IBasicPaginatedQueryResponse } from "@/Common/api/types/pagination";
import { errorLogger } from "@/Common/helpers/logger";
import { IEmployee } from "@/Common/interfaces/employee";
import { IPaginationInput } from "@/Common/types/common-queries";
import { IGetUsersByStatus, IGroupUserExtended, IUsersWithPositionResponse, TGetUsers } from "@/Layouts/components/AccessLists/interfaces";
import { apiInstance, gql } from "@/services/httpAdapter";

export async function getUsersWithPosition(locationId: string) {
	const query = gql`
		query getUsers($locationId: String!, $filter: UserFilterInput) {
			getUsers(locationId: $locationId, filter: $filter) {
				_id
				locationId
				position
				employeeId
				currentStatus {
					status
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getUsers", TGetUsers[]> = await apiInstance.post("/graphql", { query, variables });
		return response.data?.data?.getUsers;
	} catch (err) {
		errorLogger("getUsersWithPosition", err as Error);
		return null;
	}
}

export async function getUsersAndEmployeesPositions(locationId: string) {
	const query = gql`
		query getUsersAndEmployeesPositions($locationId: String!) {
			getUsersAndEmployeesPositions(locationId: $locationId) {
				activeUsersPositions
				inactiveUsersPositions
				unenrolledEmployeesPositions
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getUsersAndEmployeesPositions", IUsersWithPositionResponse> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data?.data?.getUsersAndEmployeesPositions;
	} catch (err) {
		errorLogger("getUsersAndEmployeesPositions", err as Error);
		return null;
	}
}

export async function getUsersByStatus(args: { locationId: string; status?: string; position?: string[]; cognitoStatus?: boolean }) {
	const { locationId, status, position, cognitoStatus } = args;
	const query = gql`
		query getUsers($locationId: String!, $filter: UserFilterInput) {
			getUsers(locationId: $locationId, filter: $filter) {
				_id
				employeeId
				fullName
				communications {
					communicationType
					usage
					acceptsTxt
					value
				}
				longevityDate
				hireDate
				lastLoginDateTime
				username
				position
				locationId
				cognitoStatus
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			locationId,
			status,
			position,
			cognitoStatus,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getUsers", IGetUsersByStatus[]> = await apiInstance.post("/graphql", { query, variables });
		return response.data?.data?.getUsers;
	} catch (err) {
		errorLogger("getUsersByStatus", err as Error);
		return [];
	}
}

export async function importPaychexEmployees(locationId: string) {
	const query = gql`
		query getPaychexEmployees($locationId: String!) {
			getPaychexEmployees(locationId: $locationId)
		}
	`;
	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getPaychexEmployees", boolean> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getPaychexEmployees;
	} catch (err) {
		errorLogger("importPaychexEmployees", err as Error);
		return null;
	}
}

export async function retrieveUnenrolledEmployees(locationId: string, position: string) {
	const query = gql`
		query getUnenrrolledEmployees($locationId: String!, $position: String!) {
			getUnenrrolledEmployees(locationId: $locationId, position: $position) {
				_id
				employeeId
				locationId
				hireDate
				fullName
				communications {
					communicationType
					usage
					acceptsTxt
					value
				}
			}
		}
	`;
	const variables = {
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getUnenrrolledEmployees", Partial<IEmployee>[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getUnenrrolledEmployees;
	} catch (err) {
		errorLogger("retrieveUnenrolledEmployees", err as Error);
		return null;
	}
}

export async function sendConfirmationEmailCognitoUsers(locationId: string, username: string[]) {
	const query = gql`
		mutation sendConfirmationEmailCognitoUsers($locationId: String!, $username: [String]!) {
			sendConfirmationEmailCognitoUsers(locationId: $locationId, username: $username) {
				username
			}
		}
	`;

	const variables = {
		locationId,
		username,
	};

	try {
		const response: GraphqlQueryResponse<"sendConfirmationEmailCognitoUsers", { username: string }> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.sendConfirmationEmailCognitoUsers;
	} catch (err) {
		errorLogger("sendConfirmationEmailCognitoUsers", err as Error);
		return null;
	}
}

export async function retrieveEmployee(locationId: string, employeeId: string) {
	const query = gql`
		query getEmployee($locationId: String!, $employeeId: String!) {
			getEmployee(locationId: $locationId, employeeId: $employeeId) {
				_id
				communications {
					acceptsTxt
					communicationType
					usage
					value
				}
				employeeId
				fullName
				hireDate
				jobTitle
				payrollSourceId {
					idField
					idValue
					displayId
					companyId
					providerName
				}
				payrollSource
				supervisorId
				locationId
				birthDate
				currentStatus {
					statusType
					statusReason
					effectiveDate
				}
				customFields {
					fieldId
					name
					value
				}
			}
		}
	`;

	const variables = {
		locationId,
		employeeId,
	};

	try {
		const response: GraphqlQueryResponse<"getEmployee", IEmployee> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getEmployee;
	} catch (err) {
		errorLogger("retrieveEmployee", err as Error);
		return null;
	}
}

export async function getUsersByLocationPaginated(locationId: string, userType: string, pagination: IPaginationInput) {
	const query = gql`
		query getUsersByLocationPaginated($locationId: String!, $userType: String!, $pagination: PaginationInput) {
			getUsersByLocationPaginated(locationId: $locationId, userType: $userType, pagination: $pagination) {
				items {
					_id
					fullName
					locationName
					locationId
					position
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	`;

	const variables = {
		locationId,
		userType,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<
			"getUsersByLocationPaginated",
			IBasicPaginatedQueryResponse<IGroupUserExtended>
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getUsersByLocationPaginated ?? null;
	} catch (err) {
		errorLogger(err as Error, {});
		return null;
	}
}
